import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CardContent,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

const AddBook = () => {
  const { t, i18n } = useTranslation();
  const token = localStorage.getItem("token");
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState({
    name: "",
    stock: "",
    branch_id: "",
    class_id: "",
    price: "",
  });
  const [userRole, setUserRole] = React.useState("");
  // eslint-disable-next-line no-unused-vars
  const [userBranch, setUserBranch] = React.useState("");

  React.useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role);
        setUserBranch(user.branch_id);

        // Fixed: Update formData instead of non-existent setBranch_id
        if (user.role !== "superadmin") {
          setFormData((prev) => ({
            ...prev,
            branch_id: user.branch_id.toString(),
          }));
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);
  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        "https://api.clever-school.com/api/books",
        {
          ...formData,

          class_id: parseInt(formData.class_id),
          branch_id: parseInt(formData.branch_id),
          stock: parseInt(formData.stock),
          price: parseFloat(formData.price),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(t("bookAdded"));
      navigate("/get-books");
    } catch (error) {
      message.error(t("bookAddError"));
      console.error("Submission error:", error);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      stock: "",
      branch_id: "",
      price: "",
      class_id: "",
    });
  };

  const handleChange = (name) => (e) => {
    setFormData({ ...formData, [name]: e.target.value });
  };
  const formFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: "none",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  const formSelect = {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": {
      border: "none",
      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
        direction,
        textAlign,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("addBook")}
          </Typography>
          <Grid container spacing={3}>
            {/* Book Information */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("name")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.name}
                  onChange={handleChange("name")}
                  placeholder={t("enterBookName")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>
            <Grid item xs={12} md={userRole === "superadmin" ? 4 : 6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("class")}
                </Typography>
                <Select
                  fullWidth
                  value={formData.class_id}
                  onChange={handleChange("class_id")}
                  variant="outlined"
                  displayEmpty
                  required
                  sx={formSelect}
                >
                  <MenuItem value="" disabled>
                    {t("selectClass")}
                  </MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </CardContent>
            </Grid>

            {userRole === "superadmin" && (
              <Grid item xs={12} md={6}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t("branch")}
                  </Typography>
                  <Select
                    fullWidth
                    value={formData.branch_id}
                    onChange={handleChange("branch_id")}
                    variant="outlined"
                    displayEmpty
                    required
                    sx={formSelect}
                  >
                    <MenuItem value="" disabled>
                      {t("selectBranch")}
                    </MenuItem>
                    <MenuItem value={1}>{t("branch1.1")}</MenuItem>
                    <MenuItem value={2}>{t("branch1.2")}</MenuItem>
                    <MenuItem value={3}>{t("branch1.3")}</MenuItem>
                  </Select>
                </CardContent>
              </Grid>
            )}
            {/* Financial Details */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("price")}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.price}
                  onChange={handleChange("price")}
                  placeholder={t("enterPrice")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                  InputProps={
                    {
                      // startAdornment: (
                      //   <Typography variant="body2" sx={{ mr: 1 }}>
                      //     {t("currencySymbol")}
                      //   </Typography>
                      // ),
                    }
                  }
                />
              </CardContent>
            </Grid>

            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("stock")}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.stock}
                  onChange={handleChange("stock")}
                  placeholder={t("enterStockQuantity")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    textTransform: "none",
                    color: theme.palette.info.main,
                    borderColor: theme.palette.info.main,
                  }}
                >
                  {t("reset")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/get-books")}
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    textTransform: "none",
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    px: 6,
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: theme.palette.success.main,
                    "&:hover": {
                      backgroundColor: theme.palette.success.dark,
                    },
                  }}
                >
                  {t("submit")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddBook;
